// Bill-of-materials roll-up.
//
// Phase-2 implementation: client-side, deterministic, takes a list of cabinet
// rows + the catalog collections and produces a structured BOM with totals.
// Phase-3 will move sheet-count from "area / sheet area, round up" to real
// 2D nesting; phase-2.1 may move this onto a server-side trigger once we
// fix auto-created-collection trigger registration.

import type {
  Cabinet,
  CabinetPart,
  EdgeBand,
  Hardware,
  Id,
  Material,
  USD,
} from "@/lib/types";
import { getCabinetClass } from "./index";
import {
  isDoorPart,
  isShelfPart,
  isSidePart,
  isStretcherPart,
  isBottomPart,
  isBackPart,
} from "./roles";
import { evaluateAllDrawers, drawerHardwareCounts, drawerSlideDrillings } from "./drawers";
import { legHardwareCount, legDrillings } from "./legs";

// ── Shape ────────────────────────────────────────────────────────

export interface BomMaterialLine {
  materialId: Id;
  materialName: string;
  sqFt: number;
  sheets: number;
  unitCost: USD;
  cost: USD;
}

export interface BomEdgeLine {
  edgeId: Id;
  edgeName: string;
  lft: number;
  unitCost: USD;
  cost: USD;
}

export interface BomHardwareLine {
  hardwareId: Id;
  hardwareName: string;
  kind: Hardware["kind"];
  qty: number;
  unitCost: USD;
  cost: USD;
}

export interface ProjectBom {
  materials: BomMaterialLine[];
  edges:     BomEdgeLine[];
  hardware:  BomHardwareLine[];
  partCount: number;
  totalMaterials: USD;
  totalEdges:     USD;
  totalHardware:  USD;
  grandTotal: USD;
}

// ── Catalog index ────────────────────────────────────────────────

export interface CatalogIndex {
  materials: Map<Id, Material>;
  edges:     Map<Id, EdgeBand>;
  hardware:  Map<Id, Hardware>;
  /** Default catalog ids when a cabinet has no override. */
  defaults: {
    carcassMaterialId: Id | null;
    faceMaterialId:    Id | null;
    edgeBandId:        Id | null;
    hingeId:           Id | null;
    slideId:           Id | null;
    pullId:            Id | null;
    shelfPinId:        Id | null;
  };
}

/** Picks sensible defaults from a freshly-seeded catalog set. */
export function indexCatalogs(
  materials: Material[],
  edges: EdgeBand[],
  hardware: Hardware[],
): CatalogIndex {
  const matMap = new Map(materials.map((m) => [m._id, m]));
  const edgMap = new Map(edges.map((e) => [e._id, e]));
  const hwMap  = new Map(hardware.map((h) => [h._id, h]));

  const find = <T extends { _id: Id }>(
    coll: T[],
    pred: (x: T) => boolean,
  ): Id | null => coll.find(pred)?._id ?? null;

  return {
    materials: matMap,
    edges:     edgMap,
    hardware:  hwMap,
    defaults: {
      carcassMaterialId: find(materials, (m) => m.thickness >= 0.7 && m.thickness < 0.8),
      faceMaterialId:    find(materials, (m) => m.thickness >= 0.7 && m.thickness < 0.8 && /walnut|oak|maple/i.test(m.name)),
      edgeBandId:        find(edges,     (_e) => true),
      hingeId:           find(hardware,  (h) => h.kind === "hinge"),
      slideId:           find(hardware,  (h) => h.kind === "drawer_slide"),
      pullId:            find(hardware,  (h) => h.kind === "pull" || h.kind === "knob"),
      shelfPinId:        find(hardware,  (h) => h.kind === "shelf_pin"),
    },
  };
}

// ── Material resolution (shared with nest.ts) ────────────────────

function resolveMaterial(part: CabinetPart, cab: Cabinet, cat: CatalogIndex): Id | null {
  const carcass = cab.overrides.carcassMaterialId ?? cat.defaults.carcassMaterialId;
  const face    = cab.overrides.faceMaterialId    ?? cat.defaults.faceMaterialId ?? carcass;
  if (isDoorPart(part)) return face;
  if (isBackPart(part)) return carcass; // backs are usually carcass-cheap; not a distinct material here
  return carcass;
}

/** Role-based banding policy. Doors get all four sides; sides/bottom/
 *  stretchers/shelves get the front edge only; back and toe kick stay
 *  unbanded by default. Class authors override by setting `PartDef.banding`
 *  (not yet plumbed here — Phase 5.x). */
function bandingSidesFor(p: CabinetPart, band: Id): {
  t: Id | null; b: Id | null; l: Id | null; r: Id | null;
} {
  if (isDoorPart(p)) return { t: band, b: band, l: band, r: band };
  if (isShelfPart(p) || isSidePart(p) || isBottomPart(p) || isStretcherPart(p)) {
    return { t: band, b: null, l: null, r: null };
  }
  return { t: null, b: null, l: null, r: null };
}

/** Resolve materials + per-part overrides on each part so downstream
 *  (BOM, nesting, MPR) sees authoritative ids. Overrides come from
 *  `cab.partOverrides[partId]` and take precedence over the
 *  cabinet-wide / project-wide defaults. */
export function partsFor(cab: Cabinet, cat: CatalogIndex): CabinetPart[] {
  const cls = getCabinetClass(cab.classId);
  if (!cls) return [];
  const overrides = cab.partOverrides ?? {};
  const defaultEdgeBand = cab.overrides.edgeBandId ?? cat.defaults.edgeBandId;
  const carcassParts = cls.parts({ w: cab.width, h: cab.height, d: cab.depth }).map((p) => {
    const o = overrides[p.id];
    const materialId = (o?.materialId != null
      ? o.materialId
      : resolveMaterial(p, cab, cat)) ?? 0;

    // Edge resolution: per-side override > cabinet default-band rule > null.
    // The class's default `edges` map (currently always all-null) is the
    // starting point.
    const edgeFor = (side: "t" | "b" | "l" | "r"): Id | null => {
      if (o?.edges && side in o.edges) {
        const v = o.edges[side];
        return v == null ? null : v;
      }
      return p.edges[side] ?? null;
    };

    // Apply cabinet-wide default-band by role. Mirrors the BOM allocation
    // rules below so edges in the part list match what BOM consumes.
    let edges = { t: edgeFor("t"), b: edgeFor("b"), l: edgeFor("l"), r: edgeFor("r") };
    if (defaultEdgeBand != null && !o?.edges) {
      edges = bandingSidesFor(p, defaultEdgeBand);
    }

    return { ...p, materialId, edges };
  });

  // ── Drawer parts ──
  // Drawers are nested objects on cab.drawers[]; lib/cabinets/drawers.ts
  // emits their parts list. Resolve materials + banding the same way as
  // class parts so the BOM rolls them up consistently. Drawer-face parts
  // carry `role: "door"` so isDoorPart() picks them up.
  const drawerParts = evaluateAllDrawers(cab).parts.map((p) => {
    const materialId = resolveMaterial(p, cab, cat) ?? 0;
    const edges = defaultEdgeBand != null
      ? bandingSidesFor(p, defaultEdgeBand)
      : { t: null, b: null, l: null, r: null };
    return { ...p, materialId, edges };
  });

  return [...carcassParts, ...drawerParts];
}

/** Auto-derived drillings the DSL class evaluator can't see: drawer
 *  slide-rail screws on the carcass sides + leg-mount screws on the
 *  bottom panel. Both depend on per-cabinet nested objects (drawers[],
 *  legs[]). Returned by part id; consumed by `effectiveDrillings()` plus
 *  the part previews (PartInspector / PartEditor) so the user sees the
 *  same holes the CNC will actually drill. */
export function autoDrillingsFor(cab: Cabinet, partId: string): import("./index").DrillEntry[] {
  const slides = drawerSlideDrillings(cab)[partId] ?? [];
  const legs   = legDrillings(cab)[partId] ?? [];
  return [...slides, ...legs];
}

/** Build the effective drilling list for a single part, with per-part
 *  overrides applied on top of the cabinet class's standard pattern.
 *  Returns the standard-drilling entries minus suppressed indices, plus
 *  any user-added extras and per-cabinet auto-drillings (slides, legs). */
export function effectiveDrillings(cab: Cabinet, partId: string): import("./index").DrillEntry[] {
  const cls = getCabinetClass(cab.classId);
  if (!cls) return [];
  const pattern = cls.drillPattern({ w: cab.width, h: cab.height, d: cab.depth });
  const standard = pattern[partId] ?? [];
  const o = cab.partOverrides?.[partId];
  const suppress = new Set(o?.suppressDrillings ?? []);
  const out: import("./index").DrillEntry[] = standard.filter((_, i) => !suppress.has(i));
  if (o?.extraDrillings) {
    for (const d of o.extraDrillings) {
      // `front` collapses to `back` for postprocessors that only know two
      // faces — typical shop CNCs flip the panel for front-face work
      // and treat it like a back drilling.
      out.push({
        x: d.x, y: d.y,
        diameter: d.diameter, depth: d.depth,
        face: d.face === "front" ? "back" : d.face,
        kind: d.kind,
      });
    }
  }
  // Auto-drillings are appended *after* suppression so the suppress
  // indices keep referring to the class pattern only — designers can't
  // accidentally suppress slide / leg holes through the standard list.
  out.push(...autoDrillingsFor(cab, partId));
  return out;
}

// ── Per-cabinet BOM ──────────────────────────────────────────────

interface CabinetBomTotals {
  partCount: number;
  sheetAreaByMaterial: Map<Id, number>; // sq inches
  edgeLftByBand:       Map<Id, number>;
  hardwareByModel:     Map<Id, number>;
}

function emptyTotals(): CabinetBomTotals {
  return {
    partCount: 0,
    sheetAreaByMaterial: new Map(),
    edgeLftByBand:       new Map(),
    hardwareByModel:     new Map(),
  };
}

function addArea(m: Map<Id, number>, id: Id, sqIn: number) {
  m.set(id, (m.get(id) ?? 0) + sqIn);
}

function addQty(m: Map<Id, number>, id: Id, qty: number) {
  m.set(id, (m.get(id) ?? 0) + qty);
}

/** Computes everything a single cabinet contributes to the BOM. */
export function cabinetBom(
  cab: Cabinet,
  cat: CatalogIndex,
): CabinetBomTotals {
  const cls = getCabinetClass(cab.classId);
  const totals = emptyTotals();
  if (!cls) return totals;

  const parts = partsFor(cab, cat);
  totals.partCount = parts.reduce((acc, p) => acc + (p.qty ?? 1), 0);

  const edgeBand   = cab.overrides.edgeBandId        ?? cat.defaults.edgeBandId;
  const hingeId    = cab.overrides.hingeId           ?? cat.defaults.hingeId;
  const pullId     = cab.overrides.pullId            ?? cat.defaults.pullId;
  const shelfPinId = cat.defaults.shelfPinId;
  const slideId    = cab.overrides.slideId           ?? cat.defaults.slideId;

  // Drawer faces ride on slides, not hinges — they show up with
  // role="door" so they get the 4-side banding and a pull, but the
  // hinge path below must skip them.
  const isDrawerFace = (p: CabinetPart) => p.id.startsWith("drawer_") && p.id.endsWith("_face");

  for (const p of parts) {
    const door   = isDoorPart(p);
    const shelf  = isShelfPart(p);
    if (p.materialId !== 0) {
      addArea(totals.sheetAreaByMaterial, p.materialId, p.w * p.h * (p.qty ?? 1));
    }

    if (edgeBand != null) {
      // Default banding rules (Phase 2):
      //   - doors: all 4 edges banded
      //   - shelves: front edge only (long side)
      //   - bottom + top stretchers: front edge (long)
      //   - sides: front edge only
      //   - back, toe kick: no banding
      let lft = 0;
      if (door)             lft = (2 * p.w + 2 * p.h) / 12;
      else if (shelf)       lft = p.w / 12;
      else if (isSidePart(p))      lft = p.h / 12;
      else if (isBottomPart(p) || isStretcherPart(p)) lft = p.w / 12;
      // else: no banding
      if (lft > 0) addQty(totals.edgeLftByBand, edgeBand, lft);
    }

    // Hardware allocation
    if (door && !isDrawerFace(p) && hingeId != null) {
      // Phase 2 rule: 2 hinges/door if door height <= 42", else 3.
      const hingesPerDoor = p.h <= 42 ? 2 : 3;
      addQty(totals.hardwareByModel, hingeId, hingesPerDoor);
    }
    if (door && pullId != null) {
      addQty(totals.hardwareByModel, pullId, 1);
    }
    if (shelf && shelfPinId != null) {
      addQty(totals.hardwareByModel, shelfPinId, 4); // four pins per adjustable shelf
    }
  }

  // Drawer slides — 2 per drawer in the cabinet, regardless of face role.
  // Pulls are already counted above (each drawer face is role="door").
  const hw = drawerHardwareCounts(cab);
  if (hw.slides > 0 && slideId != null) {
    addQty(totals.hardwareByModel, slideId, hw.slides);
  }

  // Legs — N per cabinet (typically 4 at corners + 1 centre-back on
  // wide units). Look up the catalog's leg model; a per-leg override
  // can specify a different SKU but the BOM rolls everything into one
  // line for now.
  const legCount = legHardwareCount(cab);
  if (legCount > 0) {
    let allocated = 0;
    for (const l of cab.legs ?? []) {
      if (l.modelId != null) {
        addQty(totals.hardwareByModel, l.modelId, 1);
        allocated++;
      }
    }
    const remainder = legCount - allocated;
    if (remainder > 0) {
      // Fall back to whichever leg model the catalog flagged as default
      // (first hardware row with kind="leg" — picked in indexCatalogs).
      const defaultLegId = [...cat.hardware.values()].find((h) => h.kind === "leg")?._id;
      if (defaultLegId != null) {
        addQty(totals.hardwareByModel, defaultLegId, remainder);
      }
    }
  }

  return totals;
}

// ── Project-level aggregation ────────────────────────────────────

export function projectBom(
  cabinets: Cabinet[],
  cat: CatalogIndex,
): ProjectBom {
  const merged = emptyTotals();
  for (const c of cabinets) {
    const t = cabinetBom(c, cat);
    merged.partCount += t.partCount;
    for (const [id, area] of t.sheetAreaByMaterial) addArea(merged.sheetAreaByMaterial, id, area);
    for (const [id, lft]  of t.edgeLftByBand)       addQty(merged.edgeLftByBand, id, lft);
    for (const [id, qty]  of t.hardwareByModel)     addQty(merged.hardwareByModel, id, qty);
  }

  const materials: BomMaterialLine[] = [];
  for (const [id, sqIn] of merged.sheetAreaByMaterial) {
    const m = cat.materials.get(id);
    if (!m) continue;
    const sheetSqIn = m.sheetW * m.sheetH;
    // 15% yield loss: real nesting waste lands in Phase 3, this is a placeholder.
    const sheets = Math.ceil((sqIn * 1.15) / sheetSqIn);
    materials.push({
      materialId: id,
      materialName: m.name,
      sqFt: sqIn / 144,
      sheets,
      unitCost: m.costPerSheet,
      cost: sheets * m.costPerSheet,
    });
  }

  const edges: BomEdgeLine[] = [];
  for (const [id, lft] of merged.edgeLftByBand) {
    const e = cat.edges.get(id);
    if (!e) continue;
    edges.push({
      edgeId: id,
      edgeName: e.name,
      lft,
      unitCost: e.costPerLft,
      cost: lft * e.costPerLft,
    });
  }

  const hardware: BomHardwareLine[] = [];
  for (const [id, qty] of merged.hardwareByModel) {
    const h = cat.hardware.get(id);
    if (!h) continue;
    hardware.push({
      hardwareId: id,
      hardwareName: `${h.brand} ${h.model}`.trim(),
      kind: h.kind,
      qty,
      unitCost: h.costEach,
      cost: qty * h.costEach,
    });
  }

  const totalMaterials = materials.reduce((a, l) => a + l.cost, 0);
  const totalEdges     = edges.reduce((a, l) => a + l.cost, 0);
  const totalHardware  = hardware.reduce((a, l) => a + l.cost, 0);

  // Stable presentation order: most-expensive first.
  materials.sort((a, b) => b.cost - a.cost);
  edges.sort((a, b) => b.cost - a.cost);
  hardware.sort((a, b) => b.cost - a.cost);

  return {
    materials,
    edges,
    hardware,
    partCount: merged.partCount,
    totalMaterials,
    totalEdges,
    totalHardware,
    grandTotal: totalMaterials + totalEdges + totalHardware,
  };
}
